import type { ReactNode } from 'react';

/**
 * MY-WORK-PRIMITIVE-CONFORMANCE-V6 -- SegmentedControl + QuickFilter: THE
 * filter grammar of a queue, measured from the founder-approved visual
 * reference (docs/design/mockups/my-work-c-system-2026-09-03.html).
 *
 * SegmentedControl: ONE primary filter control -- a sunken track holding
 * 28px segments, the pressed segment a raised surface, each segment a
 * label and a quiet count. QuickFilter: the secondary, 24px bordered
 * toggles that sit beneath it. Sized by importance under the buttons
 * (32px) and above the chips (20px); tokens.ts AC_CONTROL_HEIGHTS.
 *
 * Before this primitive the reference screen composed the same anatomy
 * from .ac-segmented over ghost AcButtons (MY-WORK-RECOMPOSE-V4); the
 * markup is now owned HERE so the next screen inherits it instead of
 * re-deriving it. PRESENTATION ONLY: options and counts arrive resolved;
 * the caller owns what a press means (the filter codec stays the page's).
 */
export interface SegmentedOption {
  readonly key: string;
  readonly label: ReactNode;
  /** A quiet count beside the label; null / undefined renders none. */
  readonly count?: number | null;
  readonly pressed: boolean;
  readonly testId?: string;
}

export interface SegmentedControlProps {
  /** Accessible name of the group (e.g. "Queue filter"). */
  readonly label: string;
  readonly options: ReadonlyArray<SegmentedOption>;
  readonly onSelect: (key: string) => void;
  readonly testId?: string;
}

export default function SegmentedControl({
  label,
  options,
  onSelect,
  testId,
}: SegmentedControlProps): JSX.Element {
  return (
    <div className="ac-segmented" role="group" aria-label={label} data-testid={testId}>
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          className="ac-segmented__btn"
          aria-pressed={o.pressed}
          onClick={() => onSelect(o.key)}
          data-testid={o.testId}
        >
          {o.label}
          {o.count !== undefined && o.count !== null ? (
            <span className="ac-segmented__n">{o.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export interface QuickFilterProps {
  readonly label: ReactNode;
  readonly count?: number | null;
  readonly pressed: boolean;
  readonly onClick: () => void;
  readonly testId?: string;
}

/** One 24px bordered toggle of the secondary filter row. */
export function QuickFilter({ label, count, pressed, onClick, testId }: QuickFilterProps): JSX.Element {
  return (
    <button
      type="button"
      className="ac-qf"
      aria-pressed={pressed}
      onClick={onClick}
      data-testid={testId}
    >
      {label}
      {count !== undefined && count !== null ? <span className="ac-qf__n">{count}</span> : null}
    </button>
  );
}

export interface QuickFiltersProps {
  readonly label: string;
  readonly children: ReactNode;
  readonly testId?: string;
}

/** The row the quick filters wrap in. */
export function QuickFilters({ label, children, testId }: QuickFiltersProps): JSX.Element {
  return (
    <div className="ac-quickfilters" role="group" aria-label={label} data-testid={testId}>
      {children}
    </div>
  );
}
